import React from "react";
import { RoughNotation } from "react-rough-notation";
import { MaskedWords, Reveal } from "../components/Reveal";
import { EXPERIENCE, SKILLS, CERTIFICATIONS } from "../data/content";
import { Star } from "../components/Scribbles";

export default function Experience() {
  return (
    <div className="px-5 md:px-10 pt-32 pb-28" data-testid="experience-page">
      <header className="max-w-5xl mb-20">
        <p className="font-handwritten text-3xl text-terracotta mb-4 rotate-[-2deg]">the journey so far</p>
        <h1 className="font-serif font-light text-6xl md:text-8xl uppercase tracking-tighter text-ink leading-none">
          <MaskedWords text="Experience" />
        </h1>
      </header>

      <div className="border-l-2 border-ink pl-6 md:pl-14 max-w-5xl">
        {EXPERIENCE.map((job, i) => (
          <Reveal key={job.company} delay={i * 0.1} className="relative pb-20 last:pb-0">
            <span className="absolute -left-[31px] md:-left-[63px] top-2 w-4 h-4 rounded-full bg-terracotta border-2 border-ink" />
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <h2 className="font-serif text-5xl md:text-7xl text-ink">
                <RoughNotation type="underline" color="#C95A3B" show strokeWidth={2} animationDelay={200 + i * 200}>
                  {job.company}
                </RoughNotation>
              </h2>
              <Star size={26} />
            </div>
            <div className="flex flex-wrap items-center gap-x-4 mt-4 text-sm uppercase tracking-widest text-muted-foreground">
              <span className="text-ink font-medium">{job.role}</span>
              <span>·</span>
              <span>{job.period}</span>
              <span>·</span>
              <span>{job.location}</span>
            </div>
            <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              {job.blurb}
            </p>
            {job.highlights && (
              <ul className="mt-5 space-y-2 max-w-2xl">
                {job.highlights.map((h, idx) => (
                  <li key={idx} className="flex gap-3 text-base text-ink/80 leading-relaxed">
                    <span className="text-terracotta shrink-0">→</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap gap-3 mt-6">
              {job.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-4 py-1 border border-ink text-xs uppercase tracking-widest text-ink"
                >
                  {tag}
                </span>
              ))}
            </div>
          </Reveal>
        ))}
      </div>

      {/* SKILLS */}
      <Reveal className="mt-24 max-w-6xl border-t-2 border-ink pt-12">
        <div className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <p className="font-handwritten text-2xl text-terracotta rotate-[-2deg]">the toolkit</p>
            <h2 className="font-serif text-4xl md:text-5xl text-ink mt-2">Skills</h2>
          </div>
          <div className="md:col-span-8 flex flex-wrap gap-3">
            {SKILLS.map((skill) => (
              <span
                key={skill}
                className="px-4 py-2 border-2 border-ink text-sm uppercase tracking-widest text-ink hover:bg-ink hover:text-sand transition-colors duration-300"
                data-testid={`skill-${skill.toLowerCase().replace(/\s/g, "-")}`}
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      </Reveal>

      {/* CERTIFICATIONS */}
      <Reveal delay={0.1} className="mt-16 max-w-6xl border-t-2 border-ink pt-12">
        <div className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <p className="font-handwritten text-2xl text-terracotta rotate-[-2deg]">on paper</p>
            <h2 className="font-serif text-4xl md:text-5xl text-ink mt-2">Certifications</h2>
          </div>
          <div className="md:col-span-8 space-y-4">
            {CERTIFICATIONS.map((c, i) => (
              <div key={i} className="border-2 border-ink p-6 bg-sand-deep" data-testid={`cert-${i}`}>
                <p className="font-serif text-xl md:text-2xl text-ink">{c.name}</p>
                <p className="text-xs uppercase tracking-widest text-terracotta mt-2">{c.issuer}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  );
}
